import { useState, type DragEvent } from 'react';
import type { Bookmark } from '@td/shared';
import {
  useBookmarks,
  useUpdateBookmark,
  useDeleteBookmark,
  useReorderBookmarks,
} from '../hooks/useBookmarks';
import { BookmarkTile } from '../components/BookmarkTile';
import { BookmarkEditor } from '../components/BookmarkEditor';
import { useConfirm } from '../components/ConfirmDialog';

export function BookmarksPage() {
  const { data, isLoading, error } = useBookmarks();
  const confirm = useConfirm();
  const update = useUpdateBookmark();
  const remove = useDeleteBookmark();
  const reorder = useReorderBookmarks();

  const [editing, setEditing] = useState<Bookmark | null>(null);
  const [creating, setCreating] = useState(false);
  const [category, setCategory] = useState<string>('All');
  const [dragId, setDragId] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;
  if (error) return <p className="text-sm text-rust">Could not load your bookmarks.</p>;

  const all = data ?? [];
  const categories = ['All', ...new Set(all.map((b) => b.category).filter(Boolean) as string[])];
  const shown = category === 'All' ? all : all.filter((b) => b.category === category);

  function onDrop(e: DragEvent, targetId: string) {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const ids = all.map((b) => b.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]!);
    reorder.mutate(ids);
    setDragId(null);
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl">Bookmarks</h1>
        <button onClick={() => setCreating(true)} className="btn-primary">
          Add bookmark
        </button>
      </header>

      {categories.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={[
                'rounded-lg px-3 py-1 text-sm transition-colors',
                category === c ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand',
              ].join(' ')}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {all.length === 0 ? (
        <div className="card max-w-md">
          <p className="text-sm text-walnut">
            Add the links you open every day — PowerSchool, your email, Google Slides, your
            curriculum guide. Star the ones you want on the dashboard.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {shown.map((b) => (
            <div
              key={b.id}
              draggable
              onDragStart={() => setDragId(b.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, b.id)}
              className="group relative"
            >
              <a href={b.url} target="_blank" rel="noopener noreferrer">
                <BookmarkTile bookmark={b} />
              </a>

              <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <button
                  onClick={() => update.mutate({ id: b.id, isQuickLink: !b.isQuickLink })}
                  title={b.isQuickLink ? 'Remove from Quick links' : 'Pin to Quick links'}
                  aria-label={b.isQuickLink ? 'Remove from Quick links' : 'Pin to Quick links'}
                  className={[
                    'rounded bg-white/90 px-1 text-sm leading-none',
                    b.isQuickLink ? 'text-ochre' : 'text-stone hover:text-ochre',
                  ].join(' ')}
                >
                  ★
                </button>
                <button
                  onClick={() => setEditing(b)}
                  aria-label={`Edit ${b.label}`}
                  className="rounded bg-white/90 px-1 text-xs leading-none text-stone hover:text-espresso"
                >
                  ✎
                </button>
                <button
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete "${b.label}"?`,
                      confirmLabel: 'Delete',
                      danger: true,
                    });
                    if (ok) remove.mutate(b.id);
                  }}
                  aria-label={`Delete ${b.label}`}
                  className="rounded bg-white/90 px-1 text-xs leading-none text-stone hover:text-rust"
                >
                  ✕
                </button>
              </div>

              {b.isQuickLink && (
                <span
                  className="pointer-events-none absolute left-1 top-1 text-sm text-ochre group-hover:opacity-0"
                  title="On the dashboard"
                >
                  ★
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <BookmarkEditor
          bookmark={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
